import { CollapsibleBox, CollapsibleBoxContainer } from '@/components/shared/CollapsibleBox';
import { adjustContentVisibility, Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

export interface FaqInfoProps {
  question: string;
  answer: string;
  analytics: string;
}

export const SectionWrapperInner = styled(Flex).attrs({
  justifyContent: 'center',
  flexDirection: 'column',
})`
  ${MEDIA_QUERIES.tabletAndDesktop`
        max-width: 1144px;
        width: 100%;
    `}
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

export interface FaqProps extends React.ComponentProps<'div'> {
  title: string;
  subtitle: string;
  data: (isMobileViewport: boolean) => FaqInfoProps[];
  darkBackground?: boolean;
}

export const Faq: React.FC<FaqProps> = ({ title, subtitle, data, ...props }) => {
  const { trackEvent } = useAnalyticsContext();
  const { isMobileViewport } = useViewportSize();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: want to track event only once
  useEffect(() => {
    if (!inView) return;
    trackEvent('car_faq_section.viewed');
  }, [inView]);

  const petFaqTrackEvent = ({ eventName, analytics }: { eventName: string; analytics: string }) =>
    trackEvent(eventName, {
      product: 'car',
      analytics,
    });

  return (
    <StyledSection pt={[30, 60]} pb={[30, 100]} px={20} {...props} ref={ref}>
      <SectionWrapperInner justifyContent="center" alignItems={['normal', 'center']}>
        <Flex mb="60px" flexDirection="column" gap="15px">
          <SectionTitle tabIndex={0}>{parse(title)}</SectionTitle>
          <SectionParagraph>{parse(subtitle)}</SectionParagraph>
        </Flex>
        <CollapsibleBoxContainer minBoxesHeight="300px">
          {data(isMobileViewport).map(({ question, answer, analytics }) => (
            <CollapsibleBox
              key={question}
              title={question}
              content={answer}
              onMouseEnter={() =>
                petFaqTrackEvent({
                  eventName: 'faq.hovered',
                  analytics,
                })
              }
              onClick={() =>
                petFaqTrackEvent({
                  eventName: 'faq.clicked',
                  analytics,
                })
              }
            />
          ))}
        </CollapsibleBoxContainer>
      </SectionWrapperInner>
    </StyledSection>
  );
};
